import { Alert, Button, Form, Modal, Radio, Space, Typography } from 'antd'
import { useState } from 'react'
import { LaunchResult } from './longRunningTasksApi'
import { getCreateTaskErrors, getRunningTasksNotices } from './longRunningTasksModel'
import { useCreateTaskMutation, useLongRunningTasksQuery } from './longRunningTasksQueries'
import {
  CreateTaskRequest,
  defaultFormValues,
  TaskType,
  TaskTypes,
  taskTypeDefinitions,
  toCreateTaskRequest,
} from './taskTypesModel'
import TaskParametersFields from './TaskParametersFields'

const { Text } = Typography

type Props = {
  onClose: () => void
  onCreated: (taskId: number, result: LaunchResult) => void
}

const CreateLongRunningTaskModal = ({ onClose, onCreated }: Props) => {
  const [form] = Form.useForm<CreateTaskRequest['values']>()
  const [selectedType, setSelectedType] = useState<TaskType>(TaskTypes[0])
  const [step, setStep] = useState<'type' | 'parameters'>('type')
  const mutation = useCreateTaskMutation()
  const runningTasksQuery = useLongRunningTasksQuery('Running')
  const runningNotices = getRunningTasksNotices(runningTasksQuery.data, selectedType)
  const createErrors = mutation.isError ? getCreateTaskErrors(mutation.error) : null

  const handleSubmit = (values: CreateTaskRequest['values']) => {
    mutation.mutate(toCreateTaskRequest(selectedType, values), {
      onSuccess: result => {
        onCreated(result.task.id, result)
      },
    })
  }

  const handleBack = () => {
    mutation.reset()
    setStep('type')
  }

  const footer = step === 'type'
    ? [
      <Button key="cancel" onClick={onClose}>Cancel</Button>,
      <Button key="next" type="primary" onClick={() => setStep('parameters')}>Next</Button>,
    ]
    : [
      <Button key="back" onClick={handleBack} disabled={mutation.isLoading}>Back</Button>,
      <Button key="submit" type="primary" onClick={() => form.submit()} loading={mutation.isLoading}>
        Start task
      </Button>,
    ]

  return (
    <Modal
      open
      title={step === 'type' ? 'New task' : `New task: ${taskTypeDefinitions[selectedType].label}`}
      onCancel={onClose}
      footer={footer}
      width={560}
    >
      {step === 'type' ? (
        <Radio.Group
          value={selectedType}
          onChange={event => setSelectedType(event.target.value)}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {TaskTypes.map(type => (
              <Radio key={type} value={type}>
                <Text strong>{taskTypeDefinitions[type].label}</Text>
                <div><Text type="secondary">{taskTypeDefinitions[type].description}</Text></div>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      ) : (
        <Form
          key={selectedType}
          form={form}
          layout="vertical"
          initialValues={defaultFormValues(selectedType)}
          onFinish={handleSubmit}
          onValuesChange={() => mutation.reset()}
        >
          <TaskParametersFields type={selectedType} />
          {createErrors && (
            <Alert
              type="error"
              showIcon
              message={createErrors.message}
              description={createErrors.errors.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {createErrors.errors.map(error => <li key={error}>{error}</li>)}
                </ul>
              )}
            />
          )}
        </Form>
      )}
      {runningNotices.map(notice => (
        <Alert key={notice.type} type={notice.type} showIcon message={notice.message} style={{ marginTop: 16 }} />
      ))}
    </Modal>
  )
}

export default CreateLongRunningTaskModal
